// Менеджер звуковых эффектов (SFX). Этап 67.
//
// ГИБРИД (решение фаундера): сейчас звуки СИНТЕЗИРУЮТСЯ программно через Web
// Audio — ноль файлов, ноль лицензий, киберпанк-эстетика «из коробки». Реестр
// SOUNDS отображает имя звука → рецепт. Позже любой звук можно подменить на
// файл (см. playFile) НЕ трогая места вызова: достаточно поменять одну строку
// реестра.
//
// Где зовётся: в основном через хелперы гаптики useTelegram (hapticTap/Success/
// Error → play('tap'/'success'/'error')) — это разом озвучивает все кнопки, что
// уже вибрируют. Боевые звуки (Animator) — отдельная будущая задача.
//
// Громкость — ползунок в настройках (corps.sfxVolume 0..100, 0 = выключено),
// миграция со старого тогла corps.sfx. AudioContext создаётся лениво и
// резюмируется по первому жесту юзера (браузер/TMA блокируют автозвук до клика).

/* eslint-disable @typescript-eslint/no-explicit-any */

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let volume = 0.6;     // 0..1, общая громкость звуков
let unlocked = false; // был ли первый жест юзера (разблокировка автозвука)

// Анти-дребезг: не чаще раза в N секунд на один и тот же звук (быстрые тапы).
const lastPlayed: Record<string, number> = {};
const MIN_GAP = 0.03; // 30 мс

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(1, v));
}

// Начальная громкость из localStorage (с миграцией со старого тогла corps.sfx).
try {
  const v = localStorage.getItem('corps.sfxVolume'); // 0..100
  if (v != null && v !== '') volume = clamp01(Number(v) / 100);
  else volume = localStorage.getItem('corps.sfx') !== '0' ? 0.6 : 0;
} catch { /* ignore */ }

function ensureCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    const AC: typeof AudioContext | undefined =
      window.AudioContext || (window as any).webkitAudioContext;
    if (!AC) return null;
    try {
      ctx = new AC();
      master = ctx.createGain();
      master.gain.value = volume;
      master.connect(ctx.destination);
    } catch {
      ctx = null;
      return null;
    }
  }
  // Браузер мог приостановить контекст — резюмируем (после жеста сработает).
  if (ctx.state === 'suspended') { ctx.resume().catch(() => { /* до жеста */ }); }
  return ctx;
}

// ─── Публичный API ────────────────────────────────────────

// Громкость 0..1 (ползунок в настройках). 0 = выключено.
export function setSfxVolume(v01: number): void {
  volume = clamp01(v01);
  if (master) master.gain.value = volume;
}

// Разблокировка автозвука по первому жесту (зовётся из MenuMusic вместе с
// разблокировкой музыки). Заодно греем кэш файловых SFX — к моменту боя/
// открытия ШК всё уже скачано (суммарно ~110 КБ).
export function unlockSfx(): void {
  if (unlocked) return;
  unlocked = true;
  ensureCtx();
  preloadSfx();
}

// ─── Файловые SFX (Этап 71) ───────────────────────────────
// «Дорогие» звуки генерируются офлайн с полноценным DSP (слои+реверб) скриптом
// app/scripts/gen-sfx.mjs → app/public/audio/sfx/*.mp3. Реестр ниже мапит имя
// звука на файл; играются через fileOr() с fallback на синтез, пока файл не
// загружен (или загрузка не удалась) — звук никогда не «молчит».

const FILE_SOUNDS: Record<string, string> = {
  hit:        '/audio/sfx/hit.mp3',
  heroDeath:  '/audio/sfx/hero_death.mp3',
  victory:    '/audio/sfx/victory.mp3',
  defeat:     '/audio/sfx/defeat.mp3',
  crateBurst: '/audio/sfx/crate_burst.mp3',
  levelUp:    '/audio/sfx/level_up.mp3',
  leagueUp:   '/audio/sfx/league_up.mp3',
  leagueDown: '/audio/sfx/league_down.mp3',
  mmFound:    '/audio/sfx/mm_found.mp3',
};

// Относительная громкость файла (файлы нормализованы к пику ≈ −1 дБ — без
// коэффициента они громче синтеза).
const FILE_GAIN: Record<string, number> = {
  hit: 0.5, heroDeath: 0.62, victory: 0.55, defeat: 0.55,
  crateBurst: 0.62, levelUp: 0.52, leagueUp: 0.55, leagueDown: 0.5,
  mmFound: 0.55,
};

interface FileEntry { buf: AudioBuffer; offset: number }
const fileCache = new Map<string, FileEntry | 'loading' | 'failed'>();

function loadFile(key: string): void {
  if (fileCache.has(key)) return;
  const url = FILE_SOUNDS[key];
  const c = ensureCtx();
  if (!url || !c) return;
  fileCache.set(key, 'loading');
  fetch(url)
    .then(r => { if (!r.ok) throw new Error(String(r.status)); return r.arrayBuffer(); })
    .then(ab => c.decodeAudioData(ab))
    .then(buf => {
      // mp3 имеет ~25-50 мс тишины в начале (encoder delay) — для ударов это
      // заметная задержка. Находим первый «звучащий» сэмпл и стартуем с него.
      const d = buf.getChannelData(0);
      let i = 0;
      while (i < d.length && Math.abs(d[i]) < 0.002) i++;
      fileCache.set(key, { buf, offset: Math.max(0, i - 32) / buf.sampleRate });
    })
    .catch(() => { fileCache.set(key, 'failed'); }); // останется синтез-fallback
}

// Прогрев кэша (все файлы или выборочно).
export function preloadSfx(keys?: string[]): void {
  for (const k of keys ?? Object.keys(FILE_SOUNDS)) loadFile(k);
}

// Рецепт «файл, а до его загрузки — синтез».
function fileOr(key: string, fallback: Recipe): Recipe {
  return (c, out, t0) => {
    const entry = fileCache.get(key);
    if (entry && typeof entry === 'object') {
      const src = c.createBufferSource();
      src.buffer = entry.buf;
      const g = c.createGain();
      g.gain.value = FILE_GAIN[key] ?? 0.5;
      src.connect(g);
      g.connect(out);
      src.start(t0, entry.offset);
      return;
    }
    if (!entry) loadFile(key); // первая просьба — запускаем загрузку на будущее
    fallback(c, out, t0);
  };
}

// ─── Синтез-примитивы ─────────────────────────────────────

// Один тон с экспоненциальной огибающей (мягкая атака → затухание).
function tone(
  c: AudioContext, out: GainNode, t0: number,
  opts: { type?: OscillatorType; f0: number; f1?: number; dur: number; gain?: number; attack?: number }
): void {
  const { type = 'triangle', f0, f1 = f0, dur, gain = 0.5, attack = 0.005 } = opts;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(f0, t0);
  if (f1 !== f0) osc.frequency.exponentialRampToValueAtTime(Math.max(1, f1), t0 + dur);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0002, gain), t0 + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g);
  g.connect(out);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

// Шумовой буфер (для ударов/смертей/свистов) — создаётся один раз на контекст.
let noiseBuf: AudioBuffer | null = null;
function getNoise(c: AudioContext): AudioBuffer {
  if (!noiseBuf || noiseBuf.sampleRate !== c.sampleRate) {
    const len = Math.floor(c.sampleRate * 0.6);
    noiseBuf = c.createBuffer(1, len, c.sampleRate);
    const d = noiseBuf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  }
  return noiseBuf;
}

// Окрашенный шум через биквад-фильтр со свипом частоты (удары, свисты, касты).
function noise(
  c: AudioContext, out: GainNode, t0: number,
  opts: { dur: number; gain?: number; filter?: BiquadFilterType; f0?: number; f1?: number; q?: number }
): void {
  const { dur, gain = 0.3, filter = 'bandpass', f0 = 1000, f1 = f0, q = 1 } = opts;
  const src = c.createBufferSource();
  src.buffer = getNoise(c);
  const flt = c.createBiquadFilter();
  flt.type = filter;
  flt.frequency.setValueAtTime(f0, t0);
  if (f1 !== f0) flt.frequency.exponentialRampToValueAtTime(Math.max(1, f1), t0 + dur);
  flt.Q.value = q;
  const g = c.createGain();
  const atk = Math.min(0.01, dur * 0.3);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0002, gain), t0 + atk);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(flt);
  flt.connect(g);
  g.connect(out);
  src.start(t0);
  src.stop(t0 + dur + 0.02);
}

// ─── Рецепты звуков ───────────────────────────────────────
// Каждый рецепт — функция, которая планирует звучание от момента t0.
// kind:'file' (плеер сэмплов) добавим точечно для «дорогих» моментов позже.

type Recipe = (c: AudioContext, out: GainNode, t0: number) => void;

const SOUNDS: Record<string, Recipe> = {
  // Лёгкий клик кнопки/навигации — короткий цифровой блип + микро-снэп сверху.
  tap: (c, out, t0) => {
    tone(c, out, t0, { type: 'triangle', f0: 900, f1: 680, dur: 0.06, gain: 0.20 });
    tone(c, out, t0, { type: 'square', f0: 2100, dur: 0.018, gain: 0.05 });
  },
  // Успех (клейм/покупка/подтверждение) — две восходящие ноты (E5 → B5).
  success: (c, out, t0) => {
    tone(c, out, t0,        { type: 'triangle', f0: 659.25, dur: 0.10, gain: 0.20 });
    tone(c, out, t0 + 0.085, { type: 'triangle', f0: 987.77, dur: 0.16, gain: 0.20 });
  },
  // Ошибка (нехватка средств/недопустимый ход) — нисходящий зуммер с гритом.
  error: (c, out, t0) => {
    tone(c, out, t0, { type: 'sawtooth', f0: 200, f1: 90, dur: 0.20, gain: 0.16 });
    tone(c, out, t0, { type: 'sawtooth', f0: 190, f1: 84, dur: 0.20, gain: 0.07 });
  },

  // ─── Бой (вешаются в AnimationLayer по типу события) ────
  // Замах атакующего (взлёт к цели) — короткий нисходящий свист.
  swing: (c, o, t) => noise(c, o, t, { dur: 0.12, gain: 0.10, filter: 'bandpass', f0: 1600, f1: 500, q: 0.7 }),
  // Попадание — файл (слои+реверб); до загрузки — «тумп» + клик синтезом.
  hit: fileOr('hit', (c, o, t) => {
    tone(c, o, t, { type: 'sine', f0: 150, f1: 60, dur: 0.10, gain: 0.32 });
    noise(c, o, t, { dur: 0.06, gain: 0.22, filter: 'highpass', f0: 1500, q: 0.5 });
  }),
  // Смерть юнита — нисходящий тон + хруст.
  death: (c, o, t) => {
    tone(c, o, t, { type: 'sawtooth', f0: 280, f1: 70, dur: 0.28, gain: 0.18 });
    noise(c, o, t, { dur: 0.20, gain: 0.14, filter: 'lowpass', f0: 1200, f1: 300, q: 0.8 });
  },
  // Смерть Героя — файл (суб-бум + рык + хвост); fallback — синтез-бум.
  heroDeath: fileOr('heroDeath', (c, o, t) => {
    tone(c, o, t, { type: 'sine', f0: 130, f1: 42, dur: 0.65, gain: 0.34 });
    tone(c, o, t + 0.04, { type: 'sawtooth', f0: 200, f1: 55, dur: 0.5, gain: 0.12 });
    noise(c, o, t, { dur: 0.6, gain: 0.16, filter: 'lowpass', f0: 900, f1: 120, q: 0.6 });
  }),
  // Призыв юнита — восходящая материализация + искра.
  summon: (c, o, t) => {
    tone(c, o, t, { type: 'triangle', f0: 280, f1: 880, dur: 0.26, gain: 0.16 });
    tone(c, o, t + 0.05, { type: 'sine', f0: 1320, dur: 0.12, gain: 0.07 });
  },
  // Розыгрыш карты-Действия / Влияния — «вжух» с восходящим свипом.
  cast: (c, o, t) => {
    noise(c, o, t, { dur: 0.22, gain: 0.12, filter: 'bandpass', f0: 400, f1: 2200, q: 0.8 });
    tone(c, o, t, { type: 'triangle', f0: 440, f1: 660, dur: 0.2, gain: 0.10 });
  },
  // Хил — мягкий восходящий арпеджио.
  heal: (c, o, t) => {
    tone(c, o, t,        { type: 'sine', f0: 660,  dur: 0.12, gain: 0.13 });
    tone(c, o, t + 0.10, { type: 'sine', f0: 880,  dur: 0.14, gain: 0.13 });
    tone(c, o, t + 0.20, { type: 'sine', f0: 1100, dur: 0.16, gain: 0.12 });
  },
  // Бафф (+статы) — яркий восходящий power-up.
  buff: (c, o, t) => {
    tone(c, o, t, { type: 'square',   f0: 440, f1: 760, dur: 0.13, gain: 0.09 });
    tone(c, o, t, { type: 'triangle', f0: 440, f1: 760, dur: 0.13, gain: 0.10 });
  },
  // Дебафф (−статы) — нисходящий «тусклый».
  debuff: (c, o, t) => {
    tone(c, o, t, { type: 'triangle', f0: 520, f1: 240, dur: 0.18, gain: 0.12 });
    tone(c, o, t, { type: 'sawtooth', f0: 200, f1: 120, dur: 0.18, gain: 0.05 });
  },
  // Карта легла в слот — приглушённый стук.
  place: (c, o, t) => {
    noise(c, o, t, { dur: 0.07, gain: 0.16, filter: 'lowpass', f0: 1200, q: 0.6 });
    tone(c, o, t, { type: 'sine', f0: 200, f1: 120, dur: 0.06, gain: 0.12 });
  },
  // Тяга карты — лёгкий шорох.
  draw: (c, o, t) => noise(c, o, t, { dur: 0.10, gain: 0.09, filter: 'highpass', f0: 2400, f1: 1200, q: 0.4 }),
  // Перемещение юнита — слайд.
  move: (c, o, t) => noise(c, o, t, { dur: 0.16, gain: 0.07, filter: 'bandpass', f0: 700, f1: 1400, q: 0.6 }),
  // Вскрытие face-down карты — цифровой блип.
  reveal: (c, o, t) => {
    tone(c, o, t,        { type: 'sine', f0: 1400, dur: 0.05, gain: 0.07 });
    tone(c, o, t + 0.04, { type: 'sine', f0: 2000, dur: 0.05, gain: 0.05 });
  },
  // +Мод / +Заражение — короткий тик.
  stack: (c, o, t) => tone(c, o, t, { type: 'square', f0: 1600, dur: 0.03, gain: 0.06 }),
  // Наложение keyword'а (Молчание и т.п.) — мягкий блип.
  blip: (c, o, t) => tone(c, o, t, { type: 'sine', f0: 1200, f1: 1500, dur: 0.05, gain: 0.07 }),
  // Смена фазы (Ф1/Ф3/Ф5 баннер) — UI-стингер.
  phase: (c, o, t) => {
    noise(c, o, t, { dur: 0.35, gain: 0.10, filter: 'bandpass', f0: 300, f1: 1800, q: 0.5 });
    tone(c, o, t + 0.02, { type: 'triangle', f0: 330, f1: 495, dur: 0.3, gain: 0.10 });
  },
  // Иммунитет Героя (блок) — металлический лязг (детюн-аккорд).
  shield: (c, o, t) => {
    tone(c, o, t, { type: 'square', f0: 1200, dur: 0.10, gain: 0.08 });
    tone(c, o, t, { type: 'square', f0: 1810, dur: 0.12, gain: 0.06 });
    tone(c, o, t, { type: 'square', f0: 2630, dur: 0.10, gain: 0.03 });
  },
  // Парашют (Герой спасся на 1 hp) — воздушный свист.
  parachute: (c, o, t) => noise(c, o, t, { dur: 0.4, gain: 0.10, filter: 'bandpass', f0: 600, f1: 1600, q: 0.4 }),
  // Стелс-активация — шиммер вниз.
  stealth: (c, o, t) => noise(c, o, t, { dur: 0.3, gain: 0.10, filter: 'bandpass', f0: 3000, f1: 600, q: 0.6 }),
  // Расчёт-пулемёт — короткая очередь.
  shot: (c, o, t) => {
    for (let k = 0; k < 3; k++) noise(c, o, t + k * 0.05, { dur: 0.04, gain: 0.16, filter: 'highpass', f0: 1800, q: 0.5 });
  },

  // ─── Конец матча (вешаются в EndOfMatchModal) ──────────
  // Победа — файл-фанфара с ревербом; fallback — синтез (C-E-G-C).
  victory: fileOr('victory', (c, o, t) => {
    [523.25, 659.25, 783.99, 1046.5].forEach((f, i) =>
      tone(c, o, t + i * 0.12, { type: 'triangle', f0: f, dur: 0.2, gain: 0.16 }));
    tone(c, o, t + 0.36, { type: 'square', f0: 1046.5, dur: 0.4, gain: 0.05 });
  }),
  // Поражение — файл (минор + тёмный хвост); fallback — синтез.
  defeat: fileOr('defeat', (c, o, t) => {
    [440, 392, 330, 262].forEach((f, i) =>
      tone(c, o, t + i * 0.16, { type: 'triangle', f0: f, dur: 0.28, gain: 0.15 }));
    tone(c, o, t, { type: 'sawtooth', f0: 130, f1: 80, dur: 0.7, gain: 0.06 });
  }),
  // Ничья — две ровные ноты.
  draw_end: (c, o, t) => {
    tone(c, o, t,        { type: 'triangle', f0: 440, dur: 0.20, gain: 0.13 });
    tone(c, o, t + 0.18, { type: 'triangle', f0: 440, dur: 0.25, gain: 0.10 });
  },

  // ─── Открытие ШК (CrateOpeningScene, Этап 71) ──────────
  // Появление контейнера — низкий гул-наезд.
  crateHum: (c, o, t) => {
    tone(c, o, t, { type: 'sawtooth', f0: 50, f1: 95, dur: 0.85, gain: 0.10, attack: 0.35 });
    noise(c, o, t, { dur: 0.85, gain: 0.05, filter: 'lowpass', f0: 280, f1: 620, q: 0.5 });
  },
  // Скан-авторизация — два цифровых бипа + тонкий «луч».
  crateScan: (c, o, t) => {
    tone(c, o, t,        { type: 'square', f0: 980,  dur: 0.07, gain: 0.06 });
    tone(c, o, t + 0.13, { type: 'square', f0: 1320, dur: 0.07, gain: 0.06 });
    noise(c, o, t, { dur: 0.55, gain: 0.035, filter: 'bandpass', f0: 2200, f1: 3800, q: 2.5 });
  },
  // Трещины + тряска — нарастающий треск + дрожь корпуса.
  crateCrack: (c, o, t) => {
    for (let k = 0; k < 4; k++)
      noise(c, o, t + k * 0.09, { dur: 0.05, gain: 0.09 + k * 0.03, filter: 'bandpass', f0: 900 + k * 420, q: 1.2 });
    tone(c, o, t, { type: 'sine', f0: 72, f1: 56, dur: 0.4, gain: 0.12 });
  },
  // Взрыв ШК — файл (удар+звон+искры); fallback — синтез-взрыв.
  crateBurst: fileOr('crateBurst', (c, o, t) => {
    tone(c, o, t, { type: 'sine', f0: 140, f1: 40, dur: 0.3, gain: 0.3 });
    noise(c, o, t, { dur: 0.5, gain: 0.2, filter: 'lowpass', f0: 4200, f1: 200, q: 0.8 });
  }),
  // Вылет одной карты из ШК — короткий тик (каскадом по картам).
  crateTick: (c, o, t) => {
    tone(c, o, t, { type: 'triangle', f0: 1450, f1: 1900, dur: 0.045, gain: 0.07 });
  },
  // Эпик/легендарка из ШК — короткий стингер «о-го».
  crateRare: (c, o, t) => {
    tone(c, o, t,        { type: 'triangle', f0: 880,    dur: 0.12, gain: 0.13 });
    tone(c, o, t + 0.09, { type: 'triangle', f0: 1318.5, dur: 0.24, gain: 0.13 });
    noise(c, o, t, { dur: 0.3, gain: 0.05, filter: 'highpass', f0: 4200, q: 0.7 });
  },
  // Финал открытия (summary) — мягкий чайм.
  crateDone: (c, o, t) => {
    tone(c, o, t,        { type: 'sine', f0: 1046.5, dur: 0.25, gain: 0.11 });
    tone(c, o, t + 0.12, { type: 'sine', f0: 1568,   dur: 0.38, gain: 0.09 });
  },

  // ─── Прогресс (модалки level-up / лига) ─────────────────
  // Level-up аккаунта — файл-арпеджио с чаймом; fallback — success-подобный.
  levelUp: fileOr('levelUp', (c, o, t) => {
    [659.25, 880, 987.77, 1318.5].forEach((f, i) =>
      tone(c, o, t + i * 0.09, { type: 'triangle', f0: f, dur: 0.16, gain: 0.14 }));
  }),
  // Подъём лиги — файл (два аккорда IV→I); fallback — фанфара покороче.
  leagueUp: fileOr('leagueUp', (c, o, t) => {
    [523.25, 659.25, 783.99].forEach((f, i) =>
      tone(c, o, t + i * 0.11, { type: 'triangle', f0: f, dur: 0.22, gain: 0.15 }));
  }),
  // Откат лиги — файл (два тона вниз + глухой удар); fallback — debuff-подобный.
  leagueDown: fileOr('leagueDown', (c, o, t) => {
    tone(c, o, t,        { type: 'triangle', f0: 660, f1: 520, dur: 0.22, gain: 0.13 });
    tone(c, o, t + 0.20, { type: 'triangle', f0: 520, f1: 392, dur: 0.30, gain: 0.13 });
    tone(c, o, t + 0.20, { type: 'sine', f0: 120, f1: 60, dur: 0.3, gain: 0.15 });
  }),

  // ─── Матчмейкинг / Война / таймеры (Этап 71) ────────────
  // Пульс поиска соперника — тихий радар-пинг (не задалбывает).
  mmPulse: (c, o, t) => {
    tone(c, o, t, { type: 'sine', f0: 700, f1: 630, dur: 0.20, gain: 0.05 });
  },
  // Соперник найден — файл (вжух+удар+стингер); fallback — синтез.
  mmFound: fileOr('mmFound', (c, o, t) => {
    noise(c, o, t, { dur: 0.25, gain: 0.12, filter: 'bandpass', f0: 500, f1: 2400, q: 0.9 });
    tone(c, o, t + 0.22, { type: 'sine', f0: 150, f1: 52, dur: 0.2, gain: 0.3 });
    tone(c, o, t + 0.26, { type: 'triangle', f0: 659.25, dur: 0.3, gain: 0.12 });
  }),
  // Снап Войны (ставка ×2/×4) — нарастающий стинг-алярм.
  warSnap: (c, o, t) => {
    tone(c, o, t,        { type: 'sawtooth', f0: 220, f1: 440, dur: 0.18, gain: 0.10 });
    tone(c, o, t + 0.02, { type: 'square',   f0: 440, f1: 880, dur: 0.14, gain: 0.045 });
  },
  // Тик последних секунд таймера фазы — короткий «часовой» щелчок.
  clockTick: (c, o, t) => {
    tone(c, o, t, { type: 'square', f0: 1900, dur: 0.025, gain: 0.05 });
    tone(c, o, t, { type: 'sine',   f0: 460,  dur: 0.05,  gain: 0.06 });
  },
};

export type SfxName = keyof typeof SOUNDS;

// Сыграть звук по имени. Безопасно вызывать когда угодно: до разблокировки/при
// выключенном звуке тихо выходит.
export function play(name: SfxName): void {
  try {
    if (volume <= 0) return;            // выключено ползунком
    const c = ensureCtx();
    if (!c || !master) return;
    if (c.state !== 'running') return;  // ещё не разблокирован — пропускаем
    const now = c.currentTime;
    const last = lastPlayed[name] || 0;
    if (now - last < MIN_GAP) return;   // анти-дребезг
    const recipe = SOUNDS[name];
    if (!recipe) return;
    lastPlayed[name] = now;
    recipe(c, master, now);
  } catch { /* звук не критичен — глушим любые ошибки */ }
}
